import React, { useCallback, useMemo, useState } from "react";
import { Button, ClickAwayListener, Icon, Typography } from "@mui/material";

export type StateCityFilterValue = {
  groupIds?: string[];
  states: string[];
  cities: string[];
};

export type StateCityFilterLocation = {
  groups?: Array<{ id: string | number; name: string }> | null;
  state?: string | number | null;
  city?: string | number | null;
};

type StateCityLocationFilterProps = {
  locations?: StateCityFilterLocation[];
  value: StateCityFilterValue;
  onApply: (_filters: StateCityFilterValue) => void;
  buttonLabel?: string;
  className?: string;
};

type StateCityAppliedFilterChipsProps = {
  locations?: StateCityFilterLocation[];
  value: StateCityFilterValue;
  onChange: (_filters: StateCityFilterValue) => void;
  className?: string;
};

export const EMPTY_STATE_CITY_FILTER: StateCityFilterValue = {
  groupIds: [],
  states: [],
  cities: [],
};

const getLocationFilterValue = (
  location: StateCityFilterLocation,
  key: "state" | "city"
) => location?.[key]?.toString().trim();

const copyStateCityFilterValue = (
  filters: StateCityFilterValue
): StateCityFilterValue => ({
  groupIds: [...(filters?.groupIds || [])],
  states: [...(filters?.states || [])],
  cities: [...(filters?.cities || [])],
});

const getUniqueLocationFilterValues = (
  locations: StateCityFilterLocation[],
  key: "state" | "city"
) => {
  return Array.from(
    new Set(
      locations
        .map((location) => getLocationFilterValue(location, key))
        .filter((filterValue): filterValue is string => Boolean(filterValue))
    )
  ).sort((a, b) => a.localeCompare(b));
};

const areStateCityFiltersEqual = (
  firstFilters: StateCityFilterValue,
  secondFilters: StateCityFilterValue
) => {
  const normalize = (values: string[] = []) => [...values].sort().join("|");

  return (
    normalize(firstFilters.groupIds) === normalize(secondFilters.groupIds) &&
    normalize(firstFilters.states) === normalize(secondFilters.states) &&
    normalize(firstFilters.cities) === normalize(secondFilters.cities)
  );
};

const hasStateCityFilters = (filters: StateCityFilterValue) =>
  (filters.groupIds?.length || 0) > 0 ||
  filters.states.length > 0 ||
  filters.cities.length > 0;

const getStateCityFilterCount = (filters: StateCityFilterValue) =>
  (filters.groupIds?.length || 0) +
  filters.states.length +
  filters.cities.length;

const getLocationGroups = (locations: StateCityFilterLocation[]) =>
  Array.from(
    new Map(
      locations.flatMap((location) =>
        (location.groups || []).map(
          (group) =>
            [
              String(group.id),
              { id: String(group.id), name: group.name },
            ] as const
        )
      )
    ).values()
  ).sort((a, b) => a.name.localeCompare(b.name));

const toggleFilterValue = (values: string[], value: string) => {
  return values.includes(value)
    ? values.filter((currentValue) => currentValue !== value)
    : [...values, value];
};

const removeFilterValue = (
  filters: StateCityFilterValue,
  filterKey: keyof StateCityFilterValue,
  filterValue: string
) => ({
  ...copyStateCityFilterValue(filters),
  [filterKey]: (filters[filterKey] || []).filter(
    (currentValue) => currentValue !== filterValue
  ),
});

export const doesLocationMatchStateCityFilter = (
  location: StateCityFilterLocation,
  filters: StateCityFilterValue
) => {
  const state = getLocationFilterValue(location, "state");
  const city = getLocationFilterValue(location, "city");
  const groupIds = filters.groupIds || [];
  const matchesGroup =
    groupIds.length === 0 ||
    (location.groups || []).some((group) =>
      groupIds.includes(String(group.id))
    );
  const matchesState =
    filters.states.length === 0 || filters.states.includes(state || "");
  const matchesCity =
    filters.cities.length === 0 || filters.cities.includes(city || "");

  return matchesGroup && matchesState && matchesCity;
};

export function StateCityAppliedFilterChips({
  locations = [],
  value,
  onChange,
  className = "",
}: StateCityAppliedFilterChipsProps) {
  if (!hasStateCityFilters(value)) {
    return null;
  }

  const groupNames = new Map(
    getLocationGroups(locations).map((group) => [group.id, group.name])
  );
  const appliedFilters = [
    ...(value.groupIds || []).map((filterValue) => ({
      key: "groupIds" as const,
      value: filterValue,
    })),
    ...value.states.map((filterValue) => ({
      key: "states" as const,
      value: filterValue,
    })),
    ...value.cities.map((filterValue) => ({
      key: "cities" as const,
      value: filterValue,
    })),
  ];

  return (
    <div className={`location-applied-filter-row ${className}`.trim()}>
      {appliedFilters.map((filter) => (
        <span
          className="location-applied-filter-chip"
          key={`${filter.key}-${filter.value}`}
        >
          <span>
            {filter.key === "groupIds"
              ? groupNames.get(filter.value) || filter.value
              : filter.value}
          </span>
          <button
            type="button"
            aria-label={`Remove ${filter.key === "groupIds" ? groupNames.get(filter.value) || filter.value : filter.value} filter`}
            onClick={() =>
              onChange(removeFilterValue(value, filter.key, filter.value))
            }
          >
            <Icon fontSize="small">close</Icon>
          </button>
        </span>
      ))}
    </div>
  );
}

export default function StateCityLocationFilter({
  locations = [],
  value,
  onApply,
  buttonLabel = "Filter",
  className = "",
}: StateCityLocationFilterProps) {
  const [open, setOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<StateCityFilterValue>(() =>
    copyStateCityFilterValue(value)
  );

  const filterOptions = useMemo(() => {
    return {
      groups: getLocationGroups(locations),
      states: getUniqueLocationFilterValues(locations, "state"),
      cities: getUniqueLocationFilterValues(locations, "city"),
    };
  }, [locations]);

  const resetDraftFilters = useCallback(() => {
    setDraftFilters(copyStateCityFilterValue(value));
  }, [value]);

  const handleToggleMenu = useCallback(() => {
    if (open) {
      resetDraftFilters();
      setOpen(false);
      return;
    }

    resetDraftFilters();
    setOpen(true);
  }, [open, resetDraftFilters]);

  const handleCloseMenu = useCallback(() => {
    if (!open) {
      return;
    }

    resetDraftFilters();
    setOpen(false);
  }, [open, resetDraftFilters]);

  const handleToggleFilterValue = useCallback(
    (filterKey: keyof StateCityFilterValue, filterValue: string) => {
      setDraftFilters((currentFilters) => ({
        ...currentFilters,
        [filterKey]: toggleFilterValue(
          currentFilters[filterKey] || [],
          filterValue
        ),
      }));
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    setDraftFilters(copyStateCityFilterValue(EMPTY_STATE_CITY_FILTER));
  }, []);

  const handleApplyFilters = useCallback(() => {
    onApply(copyStateCityFilterValue(draftFilters));
    setOpen(false);
  }, [draftFilters, onApply]);

  const hasDraftFilters = hasStateCityFilters(draftFilters);
  const hasFilterChanges = !areStateCityFiltersEqual(draftFilters, value);
  const appliedFilterCount = getStateCityFilterCount(value);
  const currentButtonLabel =
    !open && appliedFilterCount > 0
      ? `${buttonLabel} (${appliedFilterCount})`
      : buttonLabel;

  return (
    <ClickAwayListener onClickAway={handleCloseMenu}>
      <div className={`location-list-filter ${className}`.trim()}>
        <button
          type="button"
          className={`location-filter-toggle ${open ? "active" : ""}`}
          aria-expanded={open}
          onClick={handleToggleMenu}
        >
          <span>{currentButtonLabel}</span>
          <Icon fontSize="small">expand_more</Icon>
        </button>
        {open && (
          <div className="location-filter-menu">
            <div className="location-filter-menu-header">
              <Typography variant="body2" component="span" className="fw-sb">
                Filters
              </Typography>
              <Button
                color="primary"
                size="small"
                variant="contained"
                disableElevation
                disabled={!hasDraftFilters}
                onClick={handleClearFilters}
                className="location-filter-clear"
              >
                Clear Selections
              </Button>
            </div>

            {filterOptions.groups.length > 0 && (
              <div className="location-filter-group">
                <Typography
                  variant="caption"
                  component="div"
                  className="location-filter-label"
                >
                  Groups
                </Typography>
                <div className="location-filter-options">
                  {filterOptions.groups.map((group) => (
                    <button
                      type="button"
                      key={group.id}
                      className={`location-filter-chip ${draftFilters.groupIds?.includes(group.id) ? "selected" : ""}`}
                      aria-pressed={
                        draftFilters.groupIds?.includes(group.id) || false
                      }
                      onClick={() =>
                        handleToggleFilterValue("groupIds", group.id)
                      }
                    >
                      {group.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="location-filter-group">
              <Typography
                variant="caption"
                component="div"
                className="location-filter-label"
              >
                State
              </Typography>
              <div className="location-filter-options">
                {filterOptions.states.map((state) => (
                  <button
                    type="button"
                    key={state}
                    className={`location-filter-chip ${
                      draftFilters.states.includes(state) ? "selected" : ""
                    }`}
                    aria-pressed={draftFilters.states.includes(state)}
                    onClick={() => handleToggleFilterValue("states", state)}
                  >
                    {state}
                  </button>
                ))}
              </div>
            </div>

            <div className="location-filter-group">
              <Typography
                variant="caption"
                component="div"
                className="location-filter-label"
              >
                City
              </Typography>
              <div className="location-filter-options">
                {filterOptions.cities.map((city) => (
                  <button
                    type="button"
                    key={city}
                    className={`location-filter-chip ${
                      draftFilters.cities.includes(city) ? "selected" : ""
                    }`}
                    aria-pressed={draftFilters.cities.includes(city)}
                    onClick={() => handleToggleFilterValue("cities", city)}
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>

            <Button
              color="primary"
              variant="contained"
              disableElevation
              fullWidth
              disabled={!hasFilterChanges}
              onClick={handleApplyFilters}
              className="location-filter-apply"
            >
              Apply Filters
            </Button>
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
}
